// utils/theme.js

/**
 * Hook personalizado para acceder al tema del tenant actual
 */
import { useContext, createContext } from 'react';

export const ThemeContext = createContext(null);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme debe usarse dentro de ThemeProvider');
  }
  return context;
}

/**
 * Genera estilos inline basados en el color primario
 */
export function getButtonStyle(primaryColor, secondaryColor) {
  return {
    background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor || primaryColor + 'dd'})`,
    color: 'white'
  };
}

/**
 * Genera clases de Tailwind con color personalizado
 */
export function getBorderColor(primaryColor, opacity = 20) {
  return { borderColor: `${primaryColor}${opacity.toString(16).padStart(2, '0')}` };
}

export function getBackgroundColor(primaryColor, opacity = 20) {
  return { backgroundColor: `${primaryColor}${opacity.toString(16).padStart(2, '0')}` };
}

export function getTextColor(primaryColor) {
  return { color: primaryColor };
}

/**
 * Genera todas las propiedades de estilo para un componente temático
 */
export function getThemedStyles(primaryColor, secondaryColor = null) {
  return {
    button: {
      background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor || primaryColor + 'dd'})`,
      color: 'white'
    },
    border: {
      borderColor: `${primaryColor}20`
    },
    text: {
      color: primaryColor
    },
    background: {
      backgroundColor: `${primaryColor}10`
    },
    ring: {
      '--tw-ring-color': primaryColor
    }
  };
}

/**
 * Convierte HEX a RGB
 */
export function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Genera variantes de color (claro/oscuro)
 */
export function lightenColor(hex, percent) {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
  const B = Math.min(255, (num & 0x0000FF) + amt);
  
  return "#" + (
    0x1000000 +
    R * 0x10000 +
    G * 0x100 +
    B
  ).toString(16).slice(1);
}

export function darkenColor(hex, percent) {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, (num >> 16) - amt);
  const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
  const B = Math.max(0, (num & 0x0000FF) - amt);
  
  return "#" + (
    0x1000000 +
    R * 0x10000 +
    G * 0x100 +
    B
  ).toString(16).slice(1);
}

/**
 * Obtiene el color de contraste (blanco/negro) según luminosidad
 */
export function getContrastColor(hexColor) {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return '#FFFFFF';
  
  // Calcular luminosidad
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

/**
 * Validar formato HEX
 */
export function isValidHex(hex) {
  return /^#[0-9A-F]{6}$/i.test(hex);
}

function applyFaviconFromLogo(logoUrl) {
  if (!logoUrl) return;

  const href = logoUrl.includes("?")
    ? `${logoUrl}&v=${Date.now()}`
    : `${logoUrl}?v=${Date.now()}`;

  // Setear icon + shortcut icon (Chrome a veces usa uno u otro)
  ["icon", "shortcut icon"].forEach((rel) => {
    let link = document.querySelector(`link[rel='${rel}']`);
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.href = href;
    link.type = "image/png";
  });
}


/**
 * Aplicar tema al documento
 */
export function applyThemeToDocument(theme) {
  const root = document.documentElement;
  if (theme?.color_primario) {
    root.style.setProperty('--color-primary', theme.color_primario);
    root.style.setProperty('--color-primary-light', lightenColor(theme.color_primario, 20));
    root.style.setProperty('--color-primary-dark', darkenColor(theme.color_primario, 20));
  }
  
  if (theme?.color_secundario) {
    root.style.setProperty('--color-secondary', theme.color_secundario);
  }
  
  if (theme?.nombre) {
    document.title = theme.nombre;
  }

    // ✅ Favicon usando el logo del tenant
  if (theme?.logo) {
    applyFaviconFromLogo(theme.logo);
    localStorage.setItem("tenant_logo", theme.logo); // opcional
  }
}